'use client';

import React from 'react';
import styled from 'styled-components';
import Link from 'next/link';
import { theme } from '@/config/theme';
import { Skeleton } from '@/components/skeletons';

export interface Crumb {
  label: string;
  href?: string;
  isLoading?: boolean;
}

interface BreadcrumbsProps {
  items: Crumb[];
  accentColor?: string;
  rightSection?: React.ReactNode;
  style?: React.CSSProperties;
}

const SectionContainer = styled.nav`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  background: ${theme.colors.white};
  padding: 24px;
  border-radius: 0.25rem 0.25rem 0 0;
  border-bottom: none;
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 11px;
`;

const BreadcrumbContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const BreadcrumbItemStyled = styled.span<{ $clickable?: boolean; $isCurrent?: boolean }>`
  font-family: 'Open Sans', sans-serif;
  font-size: 14px;
  font-weight: ${({ $isCurrent }) => ($isCurrent ? 700 : 600)};
  line-height: 100%;
  letter-spacing: 0.24em;
  text-transform: uppercase;
  color: ${({ $isCurrent, theme }) => ($isCurrent ? theme.colors.black : theme.colors.gray[400])};
  text-decoration: none;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};

  &:hover {
    color: ${({ $clickable, $isCurrent, theme }) =>
      $isCurrent ? theme.colors.black : $clickable ? theme.colors.gray[500] : 'inherit'};
  }
`;

const BreadcrumbLink = styled(Link)<{ $isCurrent?: boolean }>`
  font-family: 'Open Sans', sans-serif;
  font-size: 14px;
  font-weight: ${({ $isCurrent }) => ($isCurrent ? 700 : 600)};
  line-height: 100%;
  letter-spacing: 0.24em;
  text-transform: uppercase;
  color: ${({ $isCurrent, theme }) => ($isCurrent ? theme.colors.black : theme.colors.gray[400])};
  text-decoration: none;
  cursor: pointer;

  &:hover {
    color: ${({ $isCurrent, theme }) => ($isCurrent ? theme.colors.black : theme.colors.gray[500])};
  }
`;

const BreadcrumbSeparator = styled.div`
  display: flex;
  align-items: center;
  color: ${theme.colors.gray[500]};
`;

const AccentLine = styled.div<{ $accentColor?: string }>`
  width: 5px;
  height: 20px;
  background: ${({ $accentColor, theme }) => $accentColor || theme.colors.primary.accent};
  border-radius: 0;
  flex-shrink: 0;
`;

const ChevronIcon: React.FC<{ size?: number; color?: string }> = ({ 
  size = 16, 
  color 
}) => {
  const defaultColor = theme.colors.gray[500];
  return (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color || defaultColor}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="9 18 15 12 9 6" />
  </svg>
  );
};

export const Breadcrumbs: React.FC<BreadcrumbsProps> = ({ items, accentColor,rightSection, style}) => {
  return (
    <SectionContainer aria-label="Breadcrumb" style={style}>
      <LeftSection>
        <AccentLine $accentColor={accentColor} />
        <BreadcrumbContainer>
          {items?.map((item, idx) => {
            const isLast = idx === items?.length - 1;
            return (
              <React.Fragment key={idx}>
                {isLast || !item.href ? (
                  <BreadcrumbItemStyled $isCurrent={isLast} $clickable={false}>
                    {item.isLoading ? (
                      <Skeleton width="120px" height="14px" variant="text" animation="pulse" />
                    ) : (
                      item.label
                    )}
                  </BreadcrumbItemStyled>
                ) : (
                  <BreadcrumbLink href={item.href} $isCurrent={false}>
                    {item.label}
                  </BreadcrumbLink>
                )}
                {!isLast && (
                  <BreadcrumbSeparator>
                    <ChevronIcon size={16} />
                  </BreadcrumbSeparator>
                )}
              </React.Fragment>
            );
          })}
        </BreadcrumbContainer>
      </LeftSection>
      {rightSection && rightSection}
    </SectionContainer>
  );
};

export default Breadcrumbs;


